import React, { ChangeEvent, useEffect, useRef } from 'react';
import { Box } from '@material-ui/core';
import { useRouteMatch } from 'react-router-dom';
import { SortOrder, UnitType, usePaginatedStockCardsQuery } from '../../../../../generated/graphql';
import { Table, TablePaginationWithCount } from '../../../../../ui-kit';
import { useWarehouseCardAction } from '../../../providers/WarehouseCardActionProvider';
import { useWarehousesScope } from '../../../providers/WarehouseScopeProvider';
import { useDebounce } from '../../../../../utils';
import { getNextOrdering } from '../../../../../ui-kit/Table/utils';
import WarehouseCardTableHeadCell from './WarehouseCardTableHeadCell';
import TableContent from './TableContent';
import { Row, getAllChecked, getSomeChecked, headCells } from './utils';
import useSearchParams from './useSearchParams';

const WarehouseCardsTable = () => {
    const {
        nameOrSku, setNameOrSku,
        selectedCategoryId, setSelectedCategoryId,
        storageUnit, setStorageUnit,
        nameSort, setNameSort,
        rowsPerPage, setRowsPerPage,
        leftoversSort, setLeftoversSort,
        page, setPage,
    } = useSearchParams();

    const { currentWarehouseId } = useWarehousesScope();
    const { currentAction, selectedCardIds, setSelectedCardIds } = useWarehouseCardAction();

    const debouncedNameOrSku = useDebounce(nameOrSku);
    const isActionActive = !!currentAction;
    const { isExact } = useRouteMatch();

    useEffect(() => {
        setPage(0);
    // eslint-disable-next-line
    }, [debouncedNameOrSku, selectedCategoryId, storageUnit, nameSort, rowsPerPage]);

    useEffect(() => {
        setSelectedCardIds([]);
    }, [isActionActive, setSelectedCardIds]);

    const offset = page * rowsPerPage;
    const { data, loading, refetch } = usePaginatedStockCardsQuery({
        variables: {
            offset,
            limit: rowsPerPage,
            warehouseIds: [currentWarehouseId as string],
            nameOrSku: debouncedNameOrSku,
            stockCardCategoryId: selectedCategoryId || undefined,
            storageUnit: storageUnit === 'null' ? undefined : UnitType[storageUnit as keyof typeof UnitType],
            stockCardNameSortOrder: nameSort ? nameSort as SortOrder : undefined,
            stockCardQuantitySortOrder: leftoversSort ? leftoversSort as SortOrder : undefined,
        },
        fetchPolicy: 'cache-and-network'
    });

    /*
        Following logic ensures we refetch table after closing update/create form
        1. isExact = true
        2. isExact = false
        3. isExact = true
        So if previous isExact is false we just returned from the update/create form
    */
    const prevIsExact = useRef(isExact);
    useEffect(() => {
        if (!prevIsExact.current) {
            // eslint-disable-next-line no-void
            void refetch();
        }
        prevIsExact.current = isExact;
    // eslint-disable-next-line
    }, [isExact]);

    const tablePagination: TablePaginationWithCount = {
        onChangePage: (_, pageNumber) => setPage(pageNumber),
        currentPage: page,
        onChangeRowsPerPage: e => setRowsPerPage(parseInt(e.target.value, 10)),
        rowsPerPage,
        count: data?.paginatedStockCards.count || 0,
    };
    const paginatedStockCards = data ? data.paginatedStockCards.items : [];

    const handleSelectAllClick = (event: ChangeEvent<HTMLInputElement>) => {
        const set = new Set<string>();
        selectedCardIds.forEach(id => set.add(id));
        if (event.target.checked) {
            paginatedStockCards.forEach(({ id }) => set.add(id));
            setSelectedCardIds(Array.from(set.values()));
            return;
        }
        paginatedStockCards.forEach(({ id }) => set.delete(id));
        setSelectedCardIds(Array.from(set.values()));
    };

    const isAllChecked = getAllChecked(selectedCardIds, paginatedStockCards);
    const areSomeChecked = getSomeChecked(selectedCardIds, paginatedStockCards);
    const isIndeterminate = areSomeChecked && !isAllChecked;

    const categoryOptions = data ? data.stockCardCategories : [];

    const onSortClick = () => {
        const currentSortOrder = !!nameSort && nameSort as SortOrder;
        const nextSort = getNextOrdering(currentSortOrder);
        setNameSort(nextSort);
    };

    return (
        <Box width="100%">
            <Table<Row>
                customHeadCellProps={{
                    indeterminate: isIndeterminate,
                    checked: isAllChecked,
                    onChange: handleSelectAllClick,
                    isActionActive,
                    name: {
                        nameSort,
                        onSortClick,
                        nameOrSku,
                        setNameOrSku,
                        setNameSort,
                    },
                    warehouseCategory: {
                        categoryOptions,
                        selectedCategoryId,
                        setSelectedCategoryId,
                    },
                    leftOvers: {
                        storageUnit,
                        setStorageUnit,
                        leftoversSort,
                        setLeftoversSort,
                    }
                }}
                tablePagination={tablePagination}
                headCells={headCells}
                TableHeadCellComponent={WarehouseCardTableHeadCell}
                isFirstColumnSticky
            >
                <TableContent
                    rowsPerPage={10}
                    stockCards={paginatedStockCards}
                    isLoading={loading && !paginatedStockCards}
                />
            </Table>
        </Box>
    );
};

export default WarehouseCardsTable;
